import React, { useState, useEffect, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import GuideButton from '../../common/guide/GuideButton';
import { infiniteRetryPolicy, startResilientConnection } from '../../../utils/signalRConnection';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  CircularProgress, 
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Pagination,
  Stack,
  TablePagination,
  useMediaQuery,
  useTheme,
  Collapse
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router-dom';
import {
  consumePendingQrOrdersFilters,
  QR_ORDERS_FILTERS_EVENT,
  QrOrdersFilterRequest,
} from '../../aichat/agent/uiBridge';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import DailyClosingModal from './DailyClosingModal';
import { useAuth } from '../../../contexts/AuthContext';
import { mainApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import { AppDatePicker } from '../../common/AppDatePicker';
import { getAccessToken } from '../../../services/auth/ownAuthSession';

// Sipariş veri tipi
interface OrderData {
  id: number;
  siparisId: string;
  companyId: string;
  qrMenuId: string;
  masaId?: number;
  kullaniciAdi: string;
  firebaseUid?: string;
  toplamTutar: number;
  siparisDetaylari: string;
  siparisOlusturmaTarihi: string;
  siparisDurumu: string;
  siparisGuncellenmeTarihi?: string;
  // İlişkili veriler
  qrMenuName?: string;
  masaNumarasi?: number;
  masaAdi?: string;
  branchId?: string;
  branchName?: string;
}

// Sipariş detay tipi
interface OrderDetail {
  urunId: string;
  urunAdi: string;
  birimFiyat: number;
  miktar: number;
  toplamFiyat: number;
  urunResmi?: string;
  aciklama?: string;
}

// Styled components
const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(10), // Alt kısma daha fazla boşluk
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  // 📱 Mobil için ayarlar
  '@media (max-width: 1499px)': {
    padding: theme.spacing(2),
    height: 'auto',
    overflowY: 'visible',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// OrdersPage arayüzü
interface OrdersPageProps {
  isManager?: boolean;
  onNavigateBack?: () => void;
  branchId?: string;
}

const OrdersPage: React.FC<OrdersPageProps> = ({ isManager = false, onNavigateBack, branchId }) => {
  const { t, i18n } = useTranslation('salesSystem');
  const numberLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const { branches } = useAuth(); // ✅ AuthContext'ten branches alınıyor
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);
  
  // Tarih aralığı filtreleri
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Sayfalama (TablePagination için 0-based)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 25 : 50);
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState<OrderData | null>(null);
  const [orderDetailModalOpen, setOrderDetailModalOpen] = useState(false);
  const [cancelConfirmModalOpen, setCancelConfirmModalOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<OrderData | null>(null);

  // SignalR connection
  const stopConnectionRef = useRef<(() => void) | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  
  // Özet kartlarını göster/gizle
  const [showSummaryCards, setShowSummaryCards] = useState(false);

  // Gün sonu modal state'leri
  const [dailyClosingModalOpen, setDailyClosingModalOpen] = useState(false);
  
  // Filtre göster/gizle state'i - Mobilde default gizli
  const [showFilters, setShowFilters] = useState(false);
  
  // Gün sonu rakamı hesaplama
  const [dailyClosingRevenue, setDailyClosingRevenue] = useState(0);

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (uid) {
        try {
          const userData = await firebaseDataService.getUserData(uid);
          if (userData?.companyId) {
            setCompanyId(userData.companyId);
          } else {
            setError(t('qrMenuOrdersPage.errors.userCompanyNotFound'));
            setLoading(false);
          }
        } catch (error) {
          console.error("Şirket ID'si alınırken hata:", error);
          setError(t('qrMenuOrdersPage.errors.companyIdLoadFailed'));
          setLoading(false);
        }
      } else {
        setError(t('qrMenuOrdersPage.errors.userNotLoggedIn'));
        setLoading(false);
      }
    };
    fetchCompanyId();
  }, []);

  // Şubeler artık AuthContext'ten alınıyor

  // Şubeler artık AuthContext'ten yükleniyor, ayrı useEffect gerekmez

  // Manager modunda şube filtresini otomatik ayarla
  useEffect(() => {
    if (isManager && branchId && branches.length > 0) {
      // Manager'ın şubesini şube filtresine ayarla
      const managerBranch = branches.find(branch => branch.id === branchId);
      if (managerBranch) {
        setBranchFilter(branchId);
        console.log(`🔒 Manager şube filtresi ayarlandı: ${managerBranch.name} (${branchId})`);
      }
    }
  }, [isManager, branchId, branches]);

  // Gün sonu rakamını hesapla - DailyClosingModal ile aynı mantık
  const calculateDailyClosingRevenue = async () => {
    if (!companyId || branchFilter === 'all') {
      setDailyClosingRevenue(0);
      return;
    }

    try {
      let lastClosingDate: string | undefined;
      try {
        const closingsResponse = await mainApi.get(
          `/api/DailyClosing?branchId=${branchFilter}&pageSize=1`
        );
        const closingsResult = closingsResponse.data;
        if (closingsResult.success && closingsResult.data && closingsResult.data.length > 0) {
          lastClosingDate = closingsResult.data[0].closingDateTime;
          console.log('🔍 [OrdersPage] API response data:', {
            rawData: closingsResult.data[0],
            lastClosingDate: lastClosingDate,
            closingDateTime: closingsResult.data[0].closingDateTime
          });
        } else {
          console.log('🔍 [OrdersPage] No daily closing found:', closingsResult);
        }
      } catch (closingErr: any) {
        console.log('❌ [OrdersPage] API error:', closingErr.response?.status);
      }

      // Şube siparişlerini filtrele
      const branchOrders = orders.filter(order => order.branchId === branchFilter);
      
      // Gün sonu tarihinden sonraki siparişleri filtrele
      let ordersAfterClosing = branchOrders;
      
      if (lastClosingDate) {
        // Gün sonu işleminin yapıldığı gerçek tarih-saati kullan
        const lastClosingDateTime = new Date(lastClosingDate);
        
        console.log('📅 [OrdersPage] Date filtering started:', {
          lastClosingDate: lastClosingDate,
          lastClosingDateTime: lastClosingDateTime.toISOString(),
          totalBranchOrders: branchOrders.length
        });
        
        ordersAfterClosing = branchOrders.filter(order => {
          const orderDate = new Date(order.siparisOlusturmaTarihi);
          const isAfterClosing = orderDate > lastClosingDateTime;
          
          console.log('📅 [OrdersPage] Date check:', {
            siparisId: order.siparisId,
            orderDate: orderDate.toISOString(),
            lastClosingDateTime: lastClosingDateTime.toISOString(),
            isAfterClosing: isAfterClosing,
            orderDateMs: orderDate.getTime(),
            closingDateMs: lastClosingDateTime.getTime(),
            diffMs: orderDate.getTime() - lastClosingDateTime.getTime()
          });
          
          return isAfterClosing;
        });
      }

      // Toplam tutarı hesapla - TÜM SİPARİŞLER DAHİL (durum fark etmez)
      const revenue = ordersAfterClosing.reduce((sum, order) => sum + order.toplamTutar, 0);
      setDailyClosingRevenue(revenue);

      console.log('💰 [OrdersPage] Gün Sonu Rakamı Hesaplandı:', {
        branchFilter: branchFilter,
        branchName: branches.find(b => b.id === branchFilter)?.name,
        lastClosingDate: lastClosingDate,
        totalBranchOrders: branchOrders.length,
        ordersAfterClosingCount: ordersAfterClosing.length,
        ordersAfterClosing: ordersAfterClosing.map(order => ({
          siparisId: order.siparisId,
          siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
          toplamTutar: order.toplamTutar,
          siparisDurumu: order.siparisDurumu
        })),
        dailyClosingRevenue: revenue,
        note: 'TÜM SİPARİŞLER GÜN SONUNA DAHİL EDİLDİ (durum fark etmez)'
      });

    } catch (error) {
      console.error('❌ Gün sonu rakamı hesaplama hatası:', error);
      setDailyClosingRevenue(0);
    }
  };

  // Şube değiştiğinde veya siparişler güncellendiğinde gün sonu rakamını hesapla
  useEffect(() => {
    calculateDailyClosingRevenue();
  }, [companyId, branchFilter, orders]);

  // Siparişleri SQL'den yükle
  const fetchOrders = async (showRefreshLoader = false) => {
    if (!companyId) return;

    if (showRefreshLoader) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const response = await mainApi.get(`/api/MasaSiparisi/orders`);
      const result = response.data;
      if (result.success && result.data) {
        const orderList: OrderData[] = result.data.map((order: any) => ({
          id: order.id,
          siparisId: order.siparisId,
          companyId: order.companyId,
          qrMenuId: order.qrMenuId,
          masaId: order.masaId,
          kullaniciAdi: order.kullaniciAdi,
          firebaseUid: order.firebaseUid,
          toplamTutar: order.toplamTutar,
          siparisDetaylari: order.siparisDetaylari,
          siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
          siparisDurumu: order.siparisDurumu,
          siparisGuncellenmeTarihi: order.siparisGuncellenmeTarihi,
          qrMenuName: order.qrMenuName,
          masaNumarasi: order.masaNumarasi,
          masaAdi: order.masaAdi,
          branchId: order.branchId,
          branchName: order.branchName
        }));
        
        orderList.sort((a, b) => new Date(b.siparisOlusturmaTarihi).getTime() - new Date(a.siparisOlusturmaTarihi).getTime());
        
        setOrders(orderList);
      } else {
        setOrders([]);
      }
    } catch (err: any) {
      console.error('❌ SQL sipariş yükleme hatası:', err);
      if (err.response?.status === 404) {
        setOrders([]);
      } else {
        setError(t('qrMenuOrdersPage.errors.ordersLoadFailed'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [companyId]);

  // AI ajan köprüsü (openQrOrdersPage): arama + durum filtrelerini doldurur.
  useEffect(() => {
    const applyAgentRequest = (req: QrOrdersFilterRequest) => {
      if (typeof req.searchText === 'string') setSearchTerm(req.searchText);
      if (typeof req.status === 'string') setStatusFilter(req.status);
    };
    const pending = consumePendingQrOrdersFilters();
    if (pending) applyAgentRequest(pending);

    const onAgentEvent = (e: Event) => {
      consumePendingQrOrdersFilters();
      applyAgentRequest((e as CustomEvent).detail as QrOrdersFilterRequest);
    };
    window.addEventListener(QR_ORDERS_FILTERS_EVENT, onAgentEvent);
    return () => window.removeEventListener(QR_ORDERS_FILTERS_EVENT, onAgentEvent);
  }, []);

  // SignalR bağlantısını kur
  useEffect(() => {
    if (companyId) {
      setupSignalRConnection(companyId);
    }

    // Cleanup: component unmount olduğunda bağlantıyı kapat
    return () => {
      closeSignalRConnection();
    };
  }, [companyId]);

  // Sipariş detaylarını parse et
  const parseOrderDetails = (detailsJson: string): OrderDetail[] => {
    try {
      return JSON.parse(detailsJson);
    } catch (error) {
      console.error('Sipariş detayları parse edilemedi:', error);
      return [];
    }
  };

  // Tarih formatla
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString(numberLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fiyat formatla
  const formatPrice = (price: number): string => {
    return `${price.toFixed(2)} ₺`;
  };

  // Sipariş durumu metnini i18n'e göre çevir (raw API değerleri Türkçe geliyor)
  const getStatusLabel = (status: string): string => {
    if (!status) return '';
    const key = status.toLowerCase();
    if (key === 'beklemede') return t('qrMenuOrdersPage.status.pending');
    if (key === 'hazırlanıyor') return t('qrMenuOrdersPage.status.preparing');
    if (key === 'hazır') return t('qrMenuOrdersPage.status.ready');
    if (key === 'teslim edildi') return t('qrMenuOrdersPage.status.delivered');
    if (key.includes('iptal')) return t('qrMenuOrdersPage.status.cancelled');
    return status;
  };

  // Durum rengini al
  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'beklemede': return 'warning';
      case 'hazırlanıyor': return 'info';
      case 'hazır': return 'success';
      case 'teslim edildi': return 'primary';
      case 'iptal': return 'error';
      default: return 'default';
    }
  };

  // Filtrelenmiş siparişler
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.siparisId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.kullaniciAdi.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.qrMenuName && order.qrMenuName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.masaAdi && order.masaAdi.toLowerCase().includes(searchTerm.toLowerCase()));
    
    
    // Durum filtresi - Türkçe karakter sorununu çöz
    let matchesStatus = false;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'iptal') {
      // Tüm varyasyonları kontrol et
      matchesStatus = order.siparisDurumu.toLowerCase().includes('iptal') || 
                     order.siparisDurumu.includes('İptal') ||
                     order.siparisDurumu === 'İptal' ||
                     order.siparisDurumu === 'iptal' ||
                     order.siparisDurumu === 'IPTAL';
    } else {
      matchesStatus = order.siparisDurumu.toLowerCase() === statusFilter.toLowerCase();
    }

    // Şube filtresi
    let matchesBranch = false;
    if (branchFilter === 'all') {
      matchesBranch = true;
    } else {
      matchesBranch = order.branchId === branchFilter;
    }

    // Tarih aralığı filtresi
    let matchesDateRange = true;
    if (startDate || endDate) {
      const orderDate = new Date(order.siparisOlusturmaTarihi);
      
      console.log('🔍 [OrdersPage] Tarih filtresi debug:', {
        siparisId: order.siparisId,
        orderDate: orderDate.toISOString(),
        orderDateLocal: orderDate.toLocaleDateString('tr-TR'),
        startDate: startDate,
        endDate: endDate,
        orderTimestamp: orderDate.getTime()
      });
      
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0); // Günün başlangıcı
        const startCheck = orderDate >= start;
        console.log('📅 Başlangıç tarihi kontrolü:', {
          startDate: start.toISOString(),
          startTimestamp: start.getTime(),
          orderTimestamp: orderDate.getTime(),
          startCheck: startCheck
        });
        matchesDateRange = matchesDateRange && startCheck;
      }
      
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Günün sonu
        const endCheck = orderDate <= end;
        console.log('📅 Bitiş tarihi kontrolü:', {
          endDate: end.toISOString(),
          endTimestamp: end.getTime(),
          orderTimestamp: orderDate.getTime(),
          endCheck: endCheck
        });
        matchesDateRange = matchesDateRange && endCheck;
      }
      
      console.log('✅ Final tarih kontrolü:', {
        siparisId: order.siparisId,
        matchesDateRange: matchesDateRange
      });
    }
    
    return matchesSearch && matchesStatus && matchesBranch && matchesDateRange;
  });

  // Sayfalama hesaplamaları (TablePagination için)
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtre değiştiğinde sayfa 0'a dön
  React.useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, branchFilter, startDate, endDate]);

  // Modal fonksiyonları
  const handleOpenOrderDetail = (order: OrderData) => {
    setSelectedOrder(order);
    setOrderDetailModalOpen(true);
  };

  const handleCloseOrderDetail = () => {
    setSelectedOrder(null);
    setOrderDetailModalOpen(false);
  };

  // Sipariş durumu güncelleme fonksiyonu
  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    if (!companyId) {
      setError(t('qrMenuOrdersPage.errors.companyInfoMissing'));
      return;
    }

    try {
      const response = await mainApi.post(`/api/MasaSiparisi/order/${orderId}/status`, {
        siparisDurumu: newStatus
      });

      const result = response.data;
      if (result.success) {
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, siparisDurumu: newStatus, siparisGuncellenmeTarihi: new Date().toISOString() }
              : order
          )
        );
        
        try {
          const storedOrders = localStorage.getItem('user_orders');
          if (storedOrders) {
            const orders = JSON.parse(storedOrders);
            const updatedOrders = orders.map((order: any) => 
              order.siparisNo === orderId || order.id === orderId
                ? { ...order, siparisDurumu: newStatus, siparisGuncellenmeTarihi: new Date().toISOString() }
                : order
            );
            localStorage.setItem('user_orders', JSON.stringify(updatedOrders));
          }
        } catch (error) {
          console.error('❌ localStorage güncelleme hatası:', error);
        }
      } else {
        throw new Error(result.message || t('qrMenuOrdersPage.errors.statusUpdateFailed'));
      }
    } catch (error: any) {
      console.error('❌ Sipariş durumu güncelleme hatası:', error);
      setError(t('qrMenuOrdersPage.errors.statusUpdateFailedWithMessage', { message: error.message }));
    }
  };

  // Hazır butonuna tıklama
  const handleMarkAsReady = async (order: OrderData) => {
    await updateOrderStatus(order.id, 'Hazır');
  };

  // Teslim Edildi butonuna tıklama
  const handleMarkAsDelivered = async (order: OrderData) => {
    await updateOrderStatus(order.id, 'Teslim Edildi');
  };

  // İptal onay modalını aç
  const handleCancelClick = (order: OrderData) => {
    setOrderToCancel(order);
    setCancelConfirmModalOpen(true);
  };

  // İptal onay modalını kapat
  const handleCancelConfirmClose = () => {
    setOrderToCancel(null);
    setCancelConfirmModalOpen(false);
  };

  // İptal işlemini onayla
  const handleConfirmCancel = async () => {
    if (orderToCancel) {
      await updateOrderStatus(orderToCancel.id, 'İptal');
      handleCancelConfirmClose();
    }
  };

  // SignalR bağlantısını kur
  const setupSignalRConnection = async (companyId: string) => {
    try {
      setConnectionStatus('connecting');
      // SignalR bağlantısı kuruluyor

      const apiUrl = process.env.REACT_APP_NETAPI_URL;
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${apiUrl}/ordershub`, {
          accessTokenFactory: async () => (await getAccessToken().catch(() => null)) ?? '',
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect(infiniteRetryPolicy)
        .configureLogging(signalR.LogLevel.None)
        .build();

      // Event handlers
      connection.on('NewOrder', (orderData: any) => {
        // Yeni sipariş bildirimi alındı
        // Sipariş detayları işleniyor
        
        // Yeni siparişi listeye ekle
        setOrders(prevOrders => {
          const newOrder: OrderData = {
            id: orderData.id,
            siparisId: orderData.siparisId,
            companyId: orderData.companyId,
            qrMenuId: orderData.qrMenuId,
            masaId: orderData.masaId,
            kullaniciAdi: orderData.kullaniciAdi,
            toplamTutar: orderData.toplamTutar,
            siparisDetaylari: orderData.siparisDetaylari,
            siparisOlusturmaTarihi: orderData.siparisOlusturmaTarihi,
            siparisDurumu: orderData.siparisDurumu,
            qrMenuName: orderData.qrMenuName,
            masaNumarasi: orderData.masaNumarasi,
            masaAdi: orderData.masaAdi,
            branchId: orderData.branchId,
            branchName: orderData.branchName
          };
          
          // Tarihe göre sıralı ekle (en yeni önce)
          const updatedOrders = [newOrder, ...prevOrders];
          return updatedOrders.sort((a, b) => 
            new Date(b.siparisOlusturmaTarihi).getTime() - new Date(a.siparisOlusturmaTarihi).getTime()
          );
        });
      });

      connection.on('OrderStatusUpdated', (orderData: any) => {
        // Sipariş durumu güncelleme bildirimi alındı
        
        // Mevcut siparişin durumunu güncelle
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderData.id 
              ? { ...order, siparisDurumu: orderData.siparisDurumu, siparisGuncellenmeTarihi: orderData.siparisGuncellenmeTarihi }
              : order
          )
        );
      });

      connection.on('OrderDeleted', (orderId: number) => {
        // Sipariş silme bildirimi alındı
        
        // Siparişi listeden kaldır
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      });

      // GroupJoined event'ini dinle
      connection.on('GroupJoined', (data: any) => {
        // Grup katılım onaylandı
      });

      // Debug için global erişim
      (window as any).signalRConnection = connection;

      // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
      // ürettiği için sunucudaki grup üyeliği kaybolur.
      stopConnectionRef.current = startResilientConnection(connection, {
        label: 'QrMenuOrders',
        onConnected: () => connection.invoke('JoinCompanyGroup', companyId),
        onStatusChange: (status) =>
          setConnectionStatus(
            status === 'Connected' ? 'connected' : status === 'Reconnecting' ? 'connecting' : 'disconnected'
          ),
      });
    } catch (error) {
      console.error('❌ SignalR bağlantı hatası:', error);
      setConnectionStatus('disconnected');
    }
  };

  // SignalR bağlantısını kapat
  const closeSignalRConnection = () => {
    stopConnectionRef.current?.();
    stopConnectionRef.current = null;
    setConnectionStatus('disconnected');
  };

  // Özet istatistikler - filtrelenmiş siparişlere göre
  const totalOrders = filteredOrders.length;
  const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.toplamTutar, 0);
  const pendingOrders = filteredOrders.filter(order => order.siparisDurumu.toLowerCase() === 'beklemede').length;

  // Şube bazlı ciro hesaplama logları
  React.useEffect(() => {
    if (branchFilter !== 'all') {
      const selectedBranch = branches.find(b => b.id === branchFilter);
      console.log('📊 [OrdersPage] Şube Ciro Hesaplaması:', {
        selectedBranch: selectedBranch?.name,
        branchId: branchFilter,
        totalFilteredOrders: filteredOrders.length,
        totalRevenue: totalRevenue,
        filteredOrdersDetails: filteredOrders.map(order => ({
          siparisId: order.siparisId,
          branchId: order.branchId,
          branchName: order.branchName,
          toplamTutar: order.toplamTutar,
          siparisDurumu: order.siparisDurumu
        }))
      });
    }
  }, [branchFilter, filteredOrders, totalRevenue, branches]);

  return (
    <ScrollableContent>
      {isMobile ? (
        // 📱 MOBİL HEADER
        <Box sx={{ mb: 2 }}>
          {/* Üst Satır: Başlık + SignalR Durumu */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={() => isManager && onNavigateBack ? onNavigateBack() : navigate('/qr-menu-management')} 
                size="small"
                sx={{ mr: 0.5 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <ReceiptIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h1" fontWeight="bold">
                {t('qrMenuOrdersPage.mobile.title')}
              </Typography>
              <GuideButton guideKey="sales.qrOrders" tabIndex={-1} sx={{ ml: 0.5 }} />
            </Box>
            {/* SignalR Bağlantı Durumu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: connectionStatus === 'connected' ? 'success.main' : 
                           connectionStatus === 'connecting' ? 'warning.main' : 'error.main'
                }} 
              />
              <Typography variant="caption" color="text.secondary">
                {connectionStatus === 'connected'
                  ? t('qrMenuOrdersPage.connection.live')
                  : connectionStatus === 'connecting'
                    ? t('qrMenuOrdersPage.connection.connectingShort')
                    : t('qrMenuOrdersPage.connection.none')}
              </Typography>
            </Box>
          </Box>
          
          {/* Alt Satır: Butonlar */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Button 
              variant="outlined" 
              size="small"
              startIcon={showSummaryCards ? <VisibilityOffIcon sx={{ fontSize: '1rem !important' }} /> : <VisibilityIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => setShowSummaryCards(!showSummaryCards)}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              {showSummaryCards ? t('qrMenuOrdersPage.actions.hide') : t('qrMenuOrdersPage.actions.summary')}
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              size="small"
              startIcon={<AssessmentIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => setDailyClosingModalOpen(true)}
              disabled={branchFilter === 'all'}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              {t('qrMenuOrdersPage.actions.endOfDay')}
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={() => fetchOrders(true)}
              disabled={refreshing}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              {t('qrMenuOrdersPage.actions.refresh')}
            </Button>
          </Box>
        </Box>
      ) : (
        // 💻 MASAÜSTÜ HEADER
        <HeaderContainer>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => isManager && onNavigateBack ? onNavigateBack() : navigate('/qr-menu-management')} 
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ReceiptIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" fontWeight="bold">
              {t('qrMenuOrdersPage.title')}
            </Typography>
            <GuideButton guideKey="sales.qrOrders" tabIndex={-1} sx={{ ml: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* SignalR Bağlantı Durumu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: connectionStatus === 'connected' ? 'success.main' : 
                           connectionStatus === 'connecting' ? 'warning.main' : 'error.main'
                }} 
              />
              <Typography variant="caption" color="text.secondary">
                {connectionStatus === 'connected'
                  ? t('qrMenuOrdersPage.connection.live')
                  : connectionStatus === 'connecting'
                    ? t('qrMenuOrdersPage.connection.connecting')
                    : t('qrMenuOrdersPage.connection.noConnection')}
              </Typography>
            </Box>
            
            <Button 
              variant="outlined" 
              startIcon={showSummaryCards ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => setShowSummaryCards(!showSummaryCards)}
            >
              {showSummaryCards ? t('qrMenuOrdersPage.actions.hideSummary') : t('qrMenuOrdersPage.actions.showSummary')}
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              startIcon={<AssessmentIcon />}
              onClick={() => setDailyClosingModalOpen(true)}
              disabled={branchFilter === 'all'}
            >
              {t('qrMenuOrdersPage.actions.endOfDay')}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={() => fetchOrders(true)}
              disabled={refreshing}
            >
              {t('qrMenuOrdersPage.actions.refresh')}
            </Button>
          </Box>
        </HeaderContainer>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Özet Kartları - Koşullu Gösterim */}
      {showSummaryCards && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('qrMenuOrdersPage.summary.totalOrders')}
                {branchFilter !== 'all' && (
                  <Typography variant="caption" display="block" color="primary.main">
                    ({branches.find(b => b.id === branchFilter)?.name || t('qrMenuOrdersPage.summary.selectedBranch')})
                  </Typography>
                )}
              </Typography>
              <Typography variant="h4" component="div">
                {totalOrders.toLocaleString(numberLocale)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('qrMenuOrdersPage.summary.totalRevenue')}
                {branchFilter !== 'all' && (
                  <Typography variant="caption" display="block" color="primary.main">
                    ({branches.find(b => b.id === branchFilter)?.name || t('qrMenuOrdersPage.summary.selectedBranch')})
                  </Typography>
                )}
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {totalRevenue.toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('qrMenuOrdersPage.summary.endOfDayAmount')}
                {branchFilter !== 'all' && (
                  <Typography variant="caption" display="block" color="primary.main">
                    ({branches.find(b => b.id === branchFilter)?.name || t('qrMenuOrdersPage.summary.selectedBranch')})
                  </Typography>
                )}
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {dailyClosingRevenue.toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('qrMenuOrdersPage.summary.pendingOrders')}
                {branchFilter !== 'all' && (
                  <Typography variant="caption" display="block" color="primary.main">
                    ({branches.find(b => b.id === branchFilter)?.name || t('qrMenuOrdersPage.summary.selectedBranch')})
                  </Typography>
                )}
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {pendingOrders.toLocaleString(numberLocale)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      )}

      {/* Filtreler Başlığı ve Göster/Gizle Butonu */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            {t('qrMenuOrdersPage.filters.title')}
          </Typography>
          {/* Aktif filtre sayısı */}
          {(searchTerm || statusFilter !== 'all' || branchFilter !== 'all' || startDate || endDate) && (
            <Chip 
              size="small" 
              label={t('qrMenuOrdersPage.filters.activeCount', {
                count: [searchTerm, statusFilter !== 'all', branchFilter !== 'all', startDate, endDate].filter(Boolean).length
              })} 
              color="primary" 
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={showFilters ? <VisibilityOffIcon fontSize="small" /> : <FilterListIcon fontSize="small" />}
          sx={{ fontSize: '0.75rem' }}
        >
          {showFilters ? t('qrMenuOrdersPage.actions.hide') : t('qrMenuOrdersPage.actions.show')}
        </Button>
      </Box>
      
      {/* Filtreler - Collapse ile göster/gizle */}
      <Collapse in={showFilters || !isMobile}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Sipariş Arama - Yarı yarıya küçültüldü */}
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('qrMenuOrdersPage.filters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Başlangıç Tarihi */}
            <Grid item xs={12} sm={6} md={2.4}>
              <AppDatePicker
                fullWidth
                size="small"
                label={t('qrMenuOrdersPage.filters.startDate')}
                value={startDate}
                onChange={setStartDate}
              />
            </Grid>
            
            {/* Bitiş Tarihi */}
            <Grid item xs={12} sm={6} md={2.4}>
              <AppDatePicker
                fullWidth
                size="small"
                label={t('qrMenuOrdersPage.filters.endDate')}
                value={endDate}
                onChange={setEndDate}
              />
            </Grid>
            
            {/* Durum Filtresi */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('qrMenuOrdersPage.filters.statusFilter')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('qrMenuOrdersPage.filters.statusFilter')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('qrMenuOrdersPage.filters.allStatuses')}</MenuItem>
                  <MenuItem value="beklemede">{t('qrMenuOrdersPage.status.pending')}</MenuItem>
                  <MenuItem value="hazır">{t('qrMenuOrdersPage.status.ready')}</MenuItem>
                  <MenuItem value="teslim edildi">{t('qrMenuOrdersPage.status.delivered')}</MenuItem>
                  <MenuItem value="iptal">{t('qrMenuOrdersPage.status.cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Şube Filtresi */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('qrMenuOrdersPage.filters.branchFilter')}</InputLabel>
                <Select
                  value={branchFilter}
                  label={t('qrMenuOrdersPage.filters.branchFilter')}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  disabled={isManager} // Manager modunda şube filtresi değiştirilemez
                >
                  <MenuItem value="all">{t('qrMenuOrdersPage.filters.allBranches')}</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Hızlı Tarih Seçimi */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setStartDate(today);
                setEndDate(today);
                console.log('📅 Bugün seçildi:', {
                  today: today,
                  startDate: today,
                  endDate: today
                });
              }}
            >
              {t('qrMenuOrdersPage.quickDates.today')}
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => {
                const today = new Date();
                const currentDay = today.getDay(); // 0 = Pazar, 1 = Pazartesi, ...
                const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // Pazartesi'ye kadar geri git
                
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() + mondayOffset);
                
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekStart.getDate() + 6);
                
                setStartDate(weekStart.toISOString().split('T')[0]);
                setEndDate(weekEnd.toISOString().split('T')[0]);
                
                console.log('📅 Bu Hafta seçildi:', {
                  weekStart: weekStart.toISOString().split('T')[0],
                  weekEnd: weekEnd.toISOString().split('T')[0]
                });
              }}
            >
              {t('qrMenuOrdersPage.quickDates.thisWeek')}
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => {
                const today = new Date();
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                setStartDate(monthStart.toISOString().split('T')[0]);
                setEndDate(monthEnd.toISOString().split('T')[0]);
              }}
            >
              {t('qrMenuOrdersPage.quickDates.thisMonth')}
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              color="secondary"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
            >
              {t('qrMenuOrdersPage.quickDates.clearDates')}
            </Button>
          </Box>
        </Paper>
      </Collapse>
      
      {/* Siparişler Tablosu */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : filteredOrders.length === 0 && !error ? (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 2 }}>
          <Typography color="text.secondary">
            {searchTerm || statusFilter !== 'all' || branchFilter !== 'all' || startDate || endDate
              ? t('qrMenuOrdersPage.emptyState.noSearchResults')
              : t('qrMenuOrdersPage.emptyState.noOrders')}
          </Typography>
        </Paper>
      ) : !error ? (
        <>
        {/* 💻 MASAÜSTÜ: Tablo Görünümü */}
        {!isMobile && (
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                      {t('qrMenuOrdersPage.table.orderId')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                      {t('qrMenuOrdersPage.table.dateTime')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                      {t('qrMenuOrdersPage.table.customer')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                      {t('qrMenuOrdersPage.table.menu')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                      {t('qrMenuOrdersPage.table.branch')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 80 }}>
                      {t('qrMenuOrdersPage.table.table')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                      {t('qrMenuOrdersPage.table.amount')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                      {t('qrMenuOrdersPage.table.status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 80 }}>
                      {t('qrMenuOrdersPage.table.actions')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 60 }}>
                      {t('qrMenuOrdersPage.table.cancel')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOrders.map((order) => (
                    <TableRow 
                      key={order.id}
                      hover
                      onClick={() => handleOpenOrderDetail(order)}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          '& .MuiTableCell-root': {
                            color: 'text.primary'
                          }
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {order.siparisId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(order.siparisOlusturmaTarihi)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.kullaniciAdi}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.qrMenuName || t('qrMenuOrdersPage.table.unknown')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.branchName || t('qrMenuOrdersPage.table.unspecified')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.masaNumarasi
                            ? t('qrMenuOrdersPage.table.tableWithNumber', { number: order.masaNumarasi })
                            : t('qrMenuOrdersPage.table.unspecified')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          {formatPrice(order.toplamTutar)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(order.siparisDurumu)} 
                          color={getStatusColor(order.siparisDurumu)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {/* Hazır Butonu - sadece beklemede ve hazırlanıyor durumlarında göster */}
                          {(order.siparisDurumu.toLowerCase() === 'beklemede' || 
                            order.siparisDurumu.toLowerCase() === 'hazırlanıyor') && (
                              <Tooltip title={t('qrMenuOrdersPage.tooltips.markReady')}>
                              <IconButton 
                                size="small"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsReady(order);
                                }}
                                sx={{ 
                                  bgcolor: 'success.light',
                                  '&:hover': { bgcolor: 'success.main' },
                                  color: 'white'
                                }}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Teslim Edildi Butonu - sadece hazır durumunda göster */}
                          {order.siparisDurumu.toLowerCase() === 'hazır' && (
                            <Tooltip title={t('qrMenuOrdersPage.tooltips.markDelivered')}>
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsDelivered(order);
                                }}
                                sx={{ 
                                  bgcolor: 'info.light',
                                  '&:hover': { bgcolor: 'info.main' },
                                  color: 'white'
                                }}
                              >
                                <TableRestaurantIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      
                      {/* İptal Sütunu */}
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {/* İptal Butonu - iptal edilmemiş siparişlerde göster */}
                          {!order.siparisDurumu.toLowerCase().includes('iptal') && (
                            <Tooltip title={t('qrMenuOrdersPage.tooltips.cancelOrder')}>
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelClick(order);
                                }}
                                sx={{ 
                                  bgcolor: 'error.light',
                                  '&:hover': { bgcolor: 'error.main' },
                                  color: 'white'
                                }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Sayfalama - Masaüstü */}
            <TablePagination
              component="div"
              count={filteredOrders.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('qrMenuOrdersPage.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('qrMenuOrdersPage.pagination.displayedRows', { from, to, count })
              }
            />
          </Paper>
        )}
        
        {/* 📱 MOBİL: Kart Görünümü */}
        {isMobile && (
          <Box sx={{ mt: 2 }}>
            {paginatedOrders.map((order) => (
              <Paper 
                key={order.id} 
                elevation={2} 
                sx={{ 
                  mb: 1.5, 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: order.siparisDurumu.toLowerCase().includes('iptal') ? 'error.light' : 'divider',
                }}
              >
                {/* Üst Kısım: Sipariş ID + Durum */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 1.5, 
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleOpenOrderDetail(order)}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      🧾 {order.siparisId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      📅 {formatDate(order.siparisOlusturmaTarihi)}
                    </Typography>
                  </Box>
                  <Chip 
                    label={getStatusLabel(order.siparisDurumu)} 
                    color={getStatusColor(order.siparisDurumu)}
                    size="small"
                  />
                </Box>
                
                {/* Orta Kısım: Bilgiler */}
                <Box 
                  sx={{ p: 1.5, cursor: 'pointer' }}
                  onClick={() => handleOpenOrderDetail(order)}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('qrMenuOrdersPage.table.customer')}</Typography>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        👤 {order.kullaniciAdi}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('qrMenuOrdersPage.table.table')}</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        🍽️ {order.masaNumarasi ? t('qrMenuOrdersPage.table.tableWithNumber', { number: order.masaNumarasi }) : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('qrMenuOrdersPage.table.branch')}</Typography>
                      <Typography variant="body2" noWrap>
                        📍 {order.branchName || t('qrMenuOrdersPage.table.unspecified')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('qrMenuOrdersPage.table.amount')}</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        💰 {formatPrice(order.toplamTutar)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Alt Kısım: Butonlar */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    p: 1.5, 
                    bgcolor: 'grey.50',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    justifyContent: 'flex-end'
                  }}
                >
                  {/* Hazır Butonu */}
                  {(order.siparisDurumu.toLowerCase() === 'beklemede' || 
                    order.siparisDurumu.toLowerCase() === 'hazırlanıyor') && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsReady(order);
                      }}
                      sx={{ fontSize: '0.7rem', py: 0.5 }}
                    >
                      {t('qrMenuOrdersPage.status.ready')}
                    </Button>
                  )}

                  {/* Teslim Edildi Butonu */}
                  {order.siparisDurumu.toLowerCase() === 'hazır' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      startIcon={<TableRestaurantIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsDelivered(order);
                      }}
                      sx={{ fontSize: '0.7rem', py: 0.5 }}
                    >
                      {t('qrMenuOrdersPage.actions.deliver')}
                    </Button>
                  )}

                  {/* Detay Butonu */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleOpenOrderDetail(order)}
                    sx={{ fontSize: '0.7rem', py: 0.5 }}
                  >
                    {t('qrMenuOrdersPage.actions.detail')}
                  </Button>

                  {/* İptal Butonu */}
                  {!order.siparisDurumu.toLowerCase().includes('iptal') && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelClick(order);
                      }}
                      sx={{ fontSize: '0.7rem', py: 0.5 }}
                    >
                      {t('qrMenuOrdersPage.status.cancelled')}
                    </Button>
                  )}
                </Box>
              </Paper>
            ))}
            
            {/* Sayfalama - Mobil */}
            <Paper sx={{ mt: 2 }}>
              <TablePagination
                component="div"
                count={filteredOrders.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={t('qrMenuOrdersPage.pagination.page')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to}/${count}`}
              />
            </Paper>
          </Box>
        )}
        </>
      ) : null}

      {/* Sipariş Detay Modal */}
      <Dialog 
        open={orderDetailModalOpen} 
        onClose={handleCloseOrderDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReceiptIcon color="primary" />
              <Box>
                <Typography variant="h6">{t('qrMenuOrdersPage.orderDetail.title')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedOrder?.siparisId}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseOrderDetail}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedOrder && (
            <Box>
              {/* Sipariş Bilgileri */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.customerName')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedOrder.kullaniciAdi}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.orderDate')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedOrder.siparisOlusturmaTarihi)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.menu')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.qrMenuName || t('qrMenuOrdersPage.table.unknown')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.branch')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.branchName || t('qrMenuOrdersPage.table.unspecified')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.table')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.masaNumarasi
                        ? t('qrMenuOrdersPage.table.tableWithNumber', { number: selectedOrder.masaNumarasi })
                        : t('qrMenuOrdersPage.table.unspecified')}
                      {selectedOrder.masaAdi && ` (${selectedOrder.masaAdi})`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.status')}
                    </Typography>
                    <Chip 
                      label={getStatusLabel(selectedOrder.siparisDurumu)} 
                      color={getStatusColor(selectedOrder.siparisDurumu)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('qrMenuOrdersPage.orderDetail.totalAmount')}
                    </Typography>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {formatPrice(selectedOrder.toplamTutar)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Sipariş Ürünleri */}
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RestaurantIcon />
                {t('qrMenuOrdersPage.orderDetail.orderItems')}
              </Typography>
              
              <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                {parseOrderDetails(selectedOrder.siparisDetaylari).map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        {/* Ürün Resmi */}
                        {item.urunResmi ? (
                          <Avatar 
                            src={item.urunResmi} 
                            alt={item.urunAdi}
                            sx={{ width: 60, height: 60 }}
                            variant="rounded"
                          />
                        ) : (
                          <Avatar 
                            sx={{ width: 60, height: 60, bgcolor: 'grey.200' }}
                            variant="rounded"
                          >
                            <RestaurantIcon color="disabled" />
                          </Avatar>
                        )}
                        
                        {/* Ürün Bilgileri */}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {item.urunAdi}
                          </Typography>
                          {item.aciklama && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {item.aciklama}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('qrMenuOrdersPage.orderDetail.unitPrice')}: {formatPrice(item.birimFiyat)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('qrMenuOrdersPage.orderDetail.quantity')}: {item.miktar}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Toplam Fiyat */}
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" color="success.main" fontWeight="bold">
                            {formatPrice(item.toplamFiyat)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < parseOrderDetails(selectedOrder.siparisDetaylari).length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {/* Toplam Özet */}
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('qrMenuOrdersPage.orderDetail.grandTotal')}
                  </Typography>
                  <Typography variant="h5" color="success.main" fontWeight="bold">
                    {formatPrice(selectedOrder.toplamTutar)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('qrMenuOrdersPage.orderDetail.itemCount', {
                    count: parseOrderDetails(selectedOrder.siparisDetaylari).length
                  })}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseOrderDetail} variant="contained">
            {t('qrMenuOrdersPage.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İptal Onay Modal */}
      <Dialog 
        open={cancelConfirmModalOpen} 
        onClose={handleCancelConfirmClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CancelIcon color="error" />
            <Typography variant="h6">{t('qrMenuOrdersPage.cancelDialog.title')}</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {orderToCancel && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('qrMenuOrdersPage.cancelDialog.description')}
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('qrMenuOrdersPage.table.orderId')}
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                  {orderToCancel.siparisId}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  {t('qrMenuOrdersPage.table.customer')}
                </Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                  {orderToCancel.kullaniciAdi}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  {t('qrMenuOrdersPage.table.amount')}
                </Typography>
                <Typography variant="body1" fontWeight="medium" color="success.main">
                  {formatPrice(orderToCancel.toplamTutar)}
                </Typography>
              </Paper>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t('qrMenuOrdersPage.cancelDialog.warningLabel')}</strong> {t('qrMenuOrdersPage.cancelDialog.warningText')}
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCancelConfirmClose} 
            variant="outlined"
          >
            {t('qrMenuOrdersPage.actions.dismiss')}
          </Button>
          <Button 
            onClick={handleConfirmCancel} 
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
          >
            {t('qrMenuOrdersPage.cancelDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gün Sonu Modal */}
      {companyId && branchFilter !== 'all' && (
        <DailyClosingModal
          open={dailyClosingModalOpen}
          onClose={() => setDailyClosingModalOpen(false)}
          companyId={companyId}
          branchId={branchFilter}
          branchName={branches.find(b => b.id === branchFilter)?.name || t('qrMenuOrdersPage.summary.selectedBranch')}
          onSuccess={() => {
            // Gün sonu başarılı olduğunda siparişleri yenile ve gün sonu rakamını hesapla
            fetchOrders(true);
            calculateDailyClosingRevenue();
          }}
        />
      )}
    </ScrollableContent>
  );
};

export default OrdersPage;
